/**
 * What the agent answers from: text the owner writes, documents they upload, links to their site.
 *
 * THE PROTOTYPE'S SHAPE ("Settings v2", Claude Design): one card of sources, newest first, each
 * a row with its kind's icon, a line of detail, a status and its actions; three ways to add one
 * in the card's head; and the rule for when two sources disagree under it. There is no on/off
 * switch - a source is there or it is removed.
 *
 * EVERY CHANGE SAVES THE MOMENT IT IS MADE, like a tool or a connection, and not with the
 * page's one bar. The Worker saves each new source as "Reading…" and answers at once, then reads
 * it (a link's pages) and makes it searchable in the background. So "Reading…" is a real row: it
 * survives a reload, and the tab re-reads itself every two seconds while any row says it. Only a
 * document's own few seconds in the browser, while its text is extracted, are a row of this
 * page's alone - that work dies with the tab anyway.
 *
 * WORDS, NOT CHARACTERS OR ANYTHING ELSE. Every count is in words, the unit an owner already
 * thinks in; the Worker converts its limits the same way.
 *
 * HOW THE AGENT USES IT (handed over whole, or searched by the agent) is our team's concern and
 * sits in its own card for an internal reader.
 */

import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { ApiError, api, post, useApi, type KnowledgePage, type KnowledgeSource } from "../../../api";
import { Dialog } from "../../Dialog";
import { Field } from "../../Form";
import { Alert, Doc, Globe, Lines, Refresh, Trash } from "../../Icons";
import { ACCEPT, ExtractError, extractText } from "../../agent/knowledge/extract";
import {
  Button,
  CardHead,
  CountTag,
  IconButton,
  Segmented,
  SettingCard,
  SettingRow,
  StatusPill,
  TeamOnlyBadge,
  TextArea,
  TextInput,
  cx,
} from "../../../ui";

type Kind = KnowledgeSource["kind"];

const ICON: Record<Kind, (p: { size?: number }) => ReactNode> = { text: Lines, file: Doc, url: Globe };

/** A source still being read: shown at the top of the list until its write answers. */
interface Pending {
  key: string;
  kind: Kind;
  title: string;
}

const words = (n: number) => n.toLocaleString("en");

function failure(err: unknown): string {
  return err instanceof ApiError || err instanceof ExtractError ? err.message : "The dashboard could not reach the server.";
}

/** "today", "yesterday", "3 days ago", then "12 Sept": how long ago, the way people say it. */
function ago(epochMs: number): string {
  const day = (ms: number) => Math.floor((ms - new Date(ms).getTimezoneOffset() * 60_000) / 86_400_000);
  const days = day(Date.now()) - day(epochMs);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(epochMs).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function size(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** The row's second line: where it came from and when. */
function detail(source: KnowledgeSource): string {
  if (source.kind === "url") {
    return [source.pages ? plural(source.pages, "page") : null, `Read ${ago(source.updatedAt)}`].filter(Boolean).join(" · ");
  }
  if (source.kind === "file") {
    return [
      source.pages ? plural(source.pages, "page") : null,
      source.bytes ? size(source.bytes) : null,
      `Added ${ago(source.createdAt)}`,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  const edited = source.updatedAt > source.createdAt + 1000;
  return `${words(source.words)} words · ${edited ? "Edited" : "Added"} ${ago(edited ? source.updatedAt : source.createdAt)}`;
}

export function KnowledgePane({ agentId, internal, onChanged }: { agentId: string; internal: boolean; onChanged: () => void }) {
  const loaded = useApi<KnowledgePage>(`/v1/agent/knowledge?agent=${encodeURIComponent(agentId)}`);
  const [fresh, setFresh] = useState<KnowledgePage | null>(null);
  const page = fresh ?? loaded.data;
  const [adding, setAdding] = useState<Kind | null>(null);
  const [editing, setEditing] = useState<KnowledgeSource | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // WHILE ANYTHING IS READING, re-read the tab. The rows are the Worker's, so this also picks up
  // a read that was started before a reload.
  const readingNow = page?.sources.some((s) => s.status === "reading") ?? false;
  useEffect(() => {
    if (!readingNow) return;
    const timer = setTimeout(() => {
      api<KnowledgePage>(`/v1/agent/knowledge?agent=${encodeURIComponent(agentId)}`)
        .then((next) => {
          setFresh(next);
          if (!next.sources.some((s) => s.status === "reading")) onChanged();
        })
        .catch(() => undefined);
    }, 2000);
    return () => clearTimeout(timer);
  }, [readingNow, page, agentId, onChanged]);

  if (!page) {
    return loaded.error ? (
      <p className="text-sm text-alert">{loaded.error.message}</p>
    ) : (
      <p className="text-sm text-ink-faint">Loading…</p>
    );
  }

  const full = page.sources.length + pending.length >= page.limits.sources;

  /** One write: its answer is the tab, and the nav's count may have moved. */
  const landed = (next: KnowledgePage) => {
    setFresh(next);
    onChanged();
  };

  /** A source read in the background, with a row that says so until it lands. */
  async function reading(item: Omit<Pending, "key">, work: () => Promise<KnowledgePage>) {
    const key = `${Date.now()}:${Math.random()}`;
    setPending((list) => [{ ...item, key }, ...list]);
    setError(null);
    try {
      landed(await work());
    } catch (err) {
      setError(`${item.title}: ${failure(err)}`);
    } finally {
      setPending((list) => list.filter((p) => p.key !== key));
    }
  }

  async function act(key: string, work: () => Promise<KnowledgePage>) {
    setBusy(key);
    setError(null);
    try {
      landed(await work());
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  const upload = (files: File[]) => {
    for (const file of files.slice(0, Math.max(0, page.limits.sources - page.sources.length - pending.length))) {
      if (file.size > page.limits.fileBytes) {
        setError(`${file.name} is over ${Math.round(page.limits.fileBytes / 1024 / 1024)} MB.`);
        continue;
      }
      void reading({ kind: "file", title: file.name }, async () => {
        const { text, pages } = await extractText(file);
        return post<KnowledgePage>("/v1/agent/knowledge/file", { agent: agentId, fileName: file.name, text, bytes: file.size, pages });
      });
    }
  };

  const addLink = (url: string, scope: "page" | "section") =>
    void act("link", () => post<KnowledgePage>("/v1/agent/knowledge/link", { agent: agentId, url, scope }));

  return (
    <>
      <SettingCard flush>
        <CardHead
          title="Sources"
          count={<CountTag n={page.sources.length} max={page.limits.sources} />}
          aside={
            <>
              <AddButton icon={<Lines size={15} />} disabled={full} onClick={() => setAdding("text")}>
                Text
              </AddButton>
              <AddButton icon={<Doc size={15} />} disabled={full} onClick={() => setAdding("file")}>
                Document
              </AddButton>
              <AddButton icon={<Globe size={15} />} disabled={full} onClick={() => setAdding("url")}>
                Link
              </AddButton>
            </>
          }
        />

        {error && (
          <p role="alert" className="mx-5 mt-4 flex gap-2 rounded-sm border border-alert-line bg-alert-soft px-3 py-2.5 text-sm text-ink">
            <Alert size={16} className="mt-px flex-none text-alert" />
            <span className="text-ink-soft">{error}</span>
          </p>
        )}

        {pending.map((item, index) => (
          <Row key={item.key} first={index === 0} kind={item.kind} title={item.title}
            detail="Reading the file…"
            status={<StatusPill tone="warm">Reading…</StatusPill>}
          />
        ))}

        {page.sources.map((source, index) => {
          const unreadable = source.status === "unreadable";
          const readingThis = source.status === "reading";
          return (
            <Row
              key={source.id}
              first={index === 0 && pending.length === 0}
              kind={source.kind}
              title={source.title}
              onOpen={source.kind === "text" ? () => setEditing(source) : undefined}
              detail={
                unreadable
                  ? (source.error ?? "This page could not be read.")
                  : readingThis && source.kind === "url"
                    ? source.words
                      ? "Reading it again…"
                      : "Reading pages…"
                    : (source.error ?? detail(source))
              }
              detailTone={unreadable ? "alert" : source.error && !readingThis ? "warm" : undefined}
              status={
                readingThis ? (
                  <StatusPill tone="warm">Reading…</StatusPill>
                ) : unreadable ? (
                  <StatusPill tone="alert">Couldn’t read</StatusPill>
                ) : source.status === "failed" ? (
                  <StatusPill tone="warm">Waiting</StatusPill>
                ) : (
                  <StatusPill tone="good">Ready</StatusPill>
                )
              }
              actions={
                confirming === source.id ? (
                  <>
                    <Button
                      type="button"
                      variant="danger"
                      disabled={busy !== null}
                      onClick={() =>
                        void act(`delete:${source.id}`, () =>
                          post<KnowledgePage>(`/v1/agent/knowledge/${encodeURIComponent(source.id)}/delete`, { agent: agentId }),
                        )
                      }
                    >
                      {busy === `delete:${source.id}` ? "Removing…" : "Remove"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </Button>
                  </>
                ) : (
                  <>
                    {source.kind === "url" && !readingThis && (
                      <IconButton
                        type="button"
                        title="Read again now"
                        disabled={busy !== null}
                        className={cx("size-8", busy === `refresh:${source.id}` && "animate-spin")}
                        onClick={() =>
                          void act(`refresh:${source.id}`, () =>
                            post<KnowledgePage>(`/v1/agent/knowledge/${encodeURIComponent(source.id)}/refresh`, { agent: agentId }),
                          )
                        }
                      >
                        <Refresh size={15} />
                        <span className="sr-only">Read again now</span>
                      </IconButton>
                    )}
                    <IconButton
                      type="button"
                      title="Remove"
                      disabled={busy !== null}
                      className="size-8 hover:bg-alert-soft hover:text-alert"
                      onClick={() => setConfirming(source.id)}
                    >
                      <Trash size={15} />
                      <span className="sr-only">Remove</span>
                    </IconButton>
                  </>
                )
              }
            />
          );
        })}

        {page.sources.length === 0 && pending.length === 0 && (
          <div className="flex flex-col items-center gap-1.5 px-5 py-10 text-center">
            <span className="text-base font-semibold text-ink">Nothing here yet</span>
            <span className="max-w-96 text-sm text-ink-soft">
              Add a price list, your FAQ page, or just write down what callers usually ask.
            </span>
          </div>
        )}
      </SettingCard>
      <div className="-mt-2 grid gap-1 text-sm text-ink-soft">
        <p>
          If two sources disagree, text you write here wins, then documents, then links. Your agent says it will check if the answer
          isn't in any of them.
        </p>
        <p className="text-xs text-ink-faint">
          About {words(page.words)} of {page.limits.wordsLabel} words used.
        </p>
      </div>

      {internal && <HowItsUsed page={page} />}

      {(adding === "text" || editing) && (
        <TextDialog
          agentId={agentId}
          source={editing}
          onClose={() => {
            setAdding(null);
            setEditing(null);
          }}
          onSaved={(next) => {
            landed(next);
            setAdding(null);
            setEditing(null);
          }}
        />
      )}
      {adding === "file" && <UploadDialog onClose={() => setAdding(null)} onFiles={upload} maxBytes={page.limits.fileBytes} />}
      {adding === "url" && (
        <LinkDialog
          pagesPerLink={page.limits.pagesPerLink}
          onClose={() => setAdding(null)}
          onAdd={(url, scope) => {
            setAdding(null);
            addLink(url, scope);
          }}
        />
      )}
    </>
  );
}

function AddButton({ icon, children, ...rest }: { icon: ReactNode; children: ReactNode; disabled?: boolean; onClick: () => void }) {
  return (
    <Button type="button" className="gap-1.5 pl-3" {...rest}>
      {icon}
      {children}
    </Button>
  );
}

function Row({
  first,
  kind,
  title,
  detail,
  detailTone,
  status,
  actions,
  onOpen,
}: {
  first: boolean;
  kind: Kind;
  title: string;
  detail: string;
  detailTone?: "alert" | "warm";
  status: ReactNode;
  actions?: ReactNode;
  onOpen?: () => void;
}) {
  const Icon = ICON[kind];
  const Name = onOpen ? "button" : "div";
  return (
    <div
      className={cx(
        "grid grid-cols-[36px_minmax(0,1fr)_auto_minmax(72px,auto)] items-center gap-3.5 px-5 py-3.5",
        // The actions are 72px on every row, one icon or two, so the status pills line up.
        !first && "border-t border-line-soft",
        "max-[720px]:grid-cols-[36px_minmax(0,1fr)_auto]",
      )}
    >
      <span className="grid size-9 place-items-center rounded-md bg-line-soft text-ink-soft">
        <Icon size={16} />
      </span>
      <Name
        {...(onOpen ? { type: "button" as const, onClick: onOpen } : {})}
        className={cx("grid min-w-0 gap-0.5 text-left", onOpen && "cursor-pointer")}
      >
        <span className="truncate text-base font-medium text-ink">{title}</span>
        <span className={cx("truncate text-sm", detailTone === "alert" ? "text-alert" : detailTone === "warm" ? "text-warm" : "text-ink-faint")}>
          {detail}
        </span>
      </Name>
      <span className="max-[720px]:hidden">{status}</span>
      <span className="flex min-w-[72px] items-center justify-end gap-0.5">{actions}</span>
    </div>
  );
}

/** Our team's view: whether the agent is handed its knowledge whole or searches it. */
function HowItsUsed({ page }: { page: KnowledgePage }) {
  const waiting = page.sources.filter((s) => s.status !== "unreadable" && !s.indexed).length;
  return (
    <SettingCard>
      <CardHead title="How the agent uses it" aside={<TeamOnlyBadge />} />
      <SettingRow first label="Mode" help={`Up to about ${words(page.limits.inlineWords)} words it is handed over whole.`}>
        <span className="text-sm text-ink">
          {page.mode === "off" && "Nothing to use"}
          {page.mode === "inline" && "Handed to the agent whole at the start of every call"}
          {page.mode === "search" && `Searched by the agent when it needs it, in ${words(page.chunks)} pieces`}
        </span>
      </SettingRow>
      {page.mode === "search" && (
        <SettingRow label="Search copy" help="Pinecone, rebuilt on every change. Failures are retried every 15 minutes.">
          <span className={cx("text-sm", waiting ? "text-warm" : "text-ink")}>
            {waiting ? `${waiting} not searchable yet` : "Up to date"}
          </span>
        </SettingRow>
      )}
      <SettingRow label="Pages read" help="Across every link.">
        <span className="text-sm text-ink">
          {page.pages} of {page.limits.pages}
        </span>
      </SettingRow>
      {!page.searchAvailable && (
        <SettingRow label="Limit" help="No Pinecone key on this deployment.">
          <span className="text-sm text-ink">Capped at {words(page.limits.inlineWords)} words</span>
        </SettingRow>
      )}
    </SettingCard>
  );
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="flex gap-2 rounded-sm border border-alert-line bg-alert-soft px-3 py-2.5 text-sm text-ink">
      <Alert size={16} className="mt-px flex-none text-alert" />
      <span className="text-ink-soft">{children}</span>
    </p>
  );
}

function TextDialog({
  agentId,
  source,
  onClose,
  onSaved,
}: {
  agentId: string;
  source: KnowledgeSource | null;
  onClose: () => void;
  onSaved: (page: KnowledgePage) => void;
}) {
  const [title, setTitle] = useState(source?.title ?? "");
  const [text, setText] = useState<string | null>(source ? null : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(false);

  // The list carries no text; an edit reads it once, when the dialog opens.
  if (source && text === null && !requested.current) {
    requested.current = true;
    api<{ source: { content: string } }>(`/v1/agent/knowledge/${encodeURIComponent(source.id)}?agent=${encodeURIComponent(agentId)}`)
      .then((answer) => setText(answer.source.content))
      .catch((err) => setError(failure(err)));
  }

  const count = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

  async function save() {
    if (!text?.trim()) return;
    setBusy(true);
    setError(null);
    // No title: the first few words, as the prototype does.
    const name = title.trim() || `${text.trim().split(/\s+/).slice(0, 5).join(" ")}…`;
    try {
      onSaved(await post<KnowledgePage>("/v1/agent/knowledge/text", { agent: agentId, id: source?.id, title: name, text }));
    } catch (err) {
      setError(failure(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={source ? "Edit text" : "Add text"}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="primary" disabled={busy || !text?.trim()} onClick={() => void save()}>
            {busy ? "Saving…" : source ? "Save changes" : "Add text"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <span className="ml-auto font-mono text-xs text-ink-faint">{words(count)} words</span>
        </>
      }
    >
      <TextInput
        autoFocus
        className="w-full font-medium"
        value={title}
        maxLength={120}
        placeholder="Title, e.g. Winter specials"
        aria-label="Title"
        onChange={(event) => setTitle(event.target.value)}
      />
      <TextArea
        className="min-h-40 w-full"
        rows={6}
        value={text ?? ""}
        disabled={text === null}
        aria-label="Text"
        placeholder={
          text === null
            ? "Loading…"
            : "Write it the way you'd explain it to a new staff member. Prices, policies, answers to questions callers ask."
        }
        onChange={(event) => setText(event.target.value)}
      />
      {error && <ErrorLine>{error}</ErrorLine>}
    </Dialog>
  );
}

function UploadDialog({ onClose, onFiles, maxBytes }: { onClose: () => void; onFiles: (files: File[]) => void; maxBytes: number }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const take = (files: FileList | null) => {
    const list = [...(files ?? [])];
    if (!list.length) return;
    onFiles(list);
    onClose();
  };
  const drag = (event: DragEvent, on: boolean) => {
    event.preventDefault();
    setOver(on);
  };

  return (
    <Dialog
      title="Upload documents"
      onClose={onClose}
      footer={
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      }
    >
      <label
        onDragOver={(event) => drag(event, true)}
        onDragLeave={(event) => drag(event, false)}
        onDrop={(event) => {
          drag(event, false);
          take(event.dataTransfer.files);
        }}
        className={cx(
          "flex cursor-pointer flex-col items-center gap-1.5 rounded-md border-[1.5px] border-dashed px-5 py-7 text-center",
          over ? "border-accent bg-accent-soft" : "border-line-strong bg-paper",
        )}
      >
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(event) => take(event.target.files)} />
        <span className="text-base font-semibold text-ink">
          Drop files here, or <span className="text-accent">choose files</span>
        </span>
        <span className="text-sm text-ink-soft">PDF or Word. Up to {Math.round(maxBytes / 1024 / 1024)} MB each.</span>
      </label>
    </Dialog>
  );
}

function LinkDialog({
  pagesPerLink,
  onClose,
  onAdd,
}: {
  pagesPerLink: number;
  onClose: () => void;
  onAdd: (url: string, scope: "page" | "section") => void;
}) {
  const [url, setUrl] = useState("");
  const [scope, setScope] = useState<"page" | "section">("section");
  const [invalid, setInvalid] = useState(false);

  const submit = () => {
    const value = url.trim();
    if (!/^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(value)) {
      setInvalid(true);
      return;
    }
    onAdd(value, scope);
  };

  return (
    <Dialog
      title="Add a link"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="primary" onClick={submit}>
            Read it
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field
        label="Web address"
        hint={
          invalid ? (
            <span className="text-alert">That doesn't look like a web address.</span>
          ) : scope === "section" ? (
            `Up to ${pagesPerLink} pages under this address, from the site's list of pages or the links on this one.`
          ) : undefined
        }
      >
        <TextInput
          autoFocus
          className={cx("w-full font-mono", invalid && "border-alert")}
          value={url}
          placeholder="https://yourbusiness.com/faq"
          onChange={(event) => {
            setUrl(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
        />
      </Field>
      <Segmented
        label="What to read"
        value={scope}
        onChange={setScope}
        options={[
          { value: "page", label: "Just this page" },
          { value: "section", label: "This page and pages under it" },
        ]}
      />
    </Dialog>
  );
}
